// src/utils/drawing/canvasSetup.js

import useStore from '../../store';

/**
 * Функция настройки размеров канвасов.
 * Вызывается при загрузке игры и при изменении размеров окна.
 */
const setupCanvasSizes = () => {
  const state = useStore.getState();
  const { ref } = state;
  const { info, convy, menu } = ref;
  const { game } = info || {};

  if (!convy) {
    // console.warn('Отсутствует convy в ref');
    return;
  }
  if (!game) {
    // console.warn('game ещё не загружен');
    return;
  }

  // 1. Логика расчета размеров (отделена от DOM)
  const calculateDimensions = () => {
    let width = 800;
    let height = 800;

    // Приоритет 1: presetSize (для старых/фиксированных игр)
    if (game.presetSize) {
      return { 
        width: game.presetSize.width, 
        height: game.presetSize.height 
      };
    }

    if (game.mode === 'sprint' || game.mode === 'mafia') {
      return { width: 800, height: 800 };
   }

    const settings = game.settings || {};
    const orientation = settings.orientation || 'square';

    // Приоритет 2: Кастомные пропорции
    if (orientation === 'custom' && settings.aspectRatio) {
      const ratio = settings.aspectRatio;
      const MAX_SIDE = 1200; // Максимальная сторона по умолчанию

      if (ratio >= 1) { // Горизонтальный или квадрат
        width = MAX_SIDE;
        height = MAX_SIDE / ratio;
      } else { // Вертикальный
        height = MAX_SIDE;
        width = MAX_SIDE * ratio;
      }
    } 
    // Приоритет 3: Старые пресеты (обратная совместимость)
    else {
      const highRes = settings.highRes;
      if (orientation === 'vertical') {
        width = 800; height = 1200;
      } else if (orientation === 'horizontal') {
        width = 1200; height = 800;
      } else {
        // square
        if (highRes) {
          width = 1280; height = 1280;
        } else {
          width = 800; height = 800;
        }
      }
    }

    return { 
      width: Math.round(width), 
      height: Math.round(height) 
    };
  };

  // 2. Расчет CSS-размеров и масштаба
  const calculateStyleDimensions = (dimensions) => {
    const PADDING_RATIO = 0.98;
    const availableWidth = window.innerWidth * PADDING_RATIO;
    const availableHeight = window.innerHeight * 0.7 * PADDING_RATIO;

    const canvasAspectRatio = dimensions.height / dimensions.width;
    
    let styleWidth = availableWidth;
    let styleHeight = styleWidth * canvasAspectRatio;

    if (styleHeight > availableHeight) {
      styleHeight = availableHeight;
      styleWidth = styleHeight / canvasAspectRatio;
    }

    return { 
      width: styleWidth, 
      height: styleHeight 
    };
  };

  // --- ВЫПОЛНЕНИЕ ---

  // А. Получаем целевые размеры
  const dimensions = calculateDimensions();
  const styleDimensions = calculateStyleDimensions(dimensions);
  
  // Б. Обновляем данные в стейте (ref.convy) НЕМЕДЛЕННО
  // Это гарантирует, что при рендере Board.jsx width={main.width} уже будет корректным
  const canvasKeys = ['main', 'bg', 'temp', 'grid', 'soft'];
  
  canvasKeys.forEach(key => {
    if (convy[key]) {
      convy[key].width = dimensions.width;
      convy[key].height = dimensions.height;
      convy[key].dimensions = dimensions;
    }
  });

  convy.styleDimensions = styleDimensions;
  convy.initialStyleDimensions = styleDimensions;
  
  convy.scale = Math.min(
    styleDimensions.width / dimensions.width,
    styleDimensions.height / dimensions.height
  );

  // В. Применяем размеры к DOM-элементам (если они уже существуют)
  const applyToDOM = (canvasObj, fill = false) => {
    if (!canvasObj || !canvasObj.ref) return;

    const canvas = canvasObj.ref;
    // Устанавливаем атрибуты width/height (важно для разрешения канваса)
    if (canvas.width !== dimensions.width) canvas.width = dimensions.width;
    if (canvas.height !== dimensions.height) canvas.height = dimensions.height;

    // Устанавливаем CSS размеры
    canvas.style.width = `${styleDimensions.width}px`;
    canvas.style.height = `${styleDimensions.height}px`;

    // Настраиваем контекст
    const ctx = canvas.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (fill) {
      ctx.fillStyle = game.settings?.backgroundColor || '#FFFFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  };

  applyToDOM(convy.main, true); // main заливаем фоном
  applyToDOM(convy.bg);
  applyToDOM(convy.temp);
  applyToDOM(convy.grid);
  applyToDOM(convy.soft);

  // Г. Сигнализируем, что инициализация прошла (если есть ref, значит мы в компоненте)
  if (convy.main && convy.main.ref) {
    useStore.setState({ canvasInitialized: true });
    ref.drawing.methods.redraw?.();
    menu.methods.render?.()
  }
};

// Регистрируем слушатели один раз при импорте
if (typeof window !== 'undefined') {
  let resizeTimeout;
  const onResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(setupCanvasSizes, 100);
  };

  window.addEventListener('resize', onResize);
  
  if (window.Telegram?.WebApp) {
    window.Telegram.WebApp.onEvent('viewportChanged', onResize);
  }
}

export default setupCanvasSizes;